import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithProvider: (provider: 'github' | 'twitter') => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const authStatus = localStorage.getItem('isAuthenticated');
    const savedUsername = localStorage.getItem('username');
    const adminStatus = localStorage.getItem('isAdmin');
    
    if (authStatus === 'true' && savedUsername) {
      setIsAuthenticated(true);
      setUsername(savedUsername);
      setIsAdmin(adminStatus === 'true');
    }
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    // User credentials - you can add more users here
    const users = [
      { username: 'admin', email: 'admin@bountrust.com', password: 'admin123', isAdmin: true },
      { username: 'ahmet', email: 'ahmet@bountrust.com', password: 'salihlili45', isAdmin: false }
    ];

    const user = users.find(u => u.email === email && u.password === password);
    
    if (user) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('username', user.username);
      localStorage.setItem('isAdmin', user.isAdmin.toString());
      setIsAuthenticated(true);
      setUsername(user.username);
      setIsAdmin(user.isAdmin);
    } else {
      throw new Error('Invalid email or password');
    }
  };

  const loginWithProvider = async (provider: 'github' | 'twitter'): Promise<void> => {
    // Mock social login
    const mockUser = { username: `${provider}_user`, isAdmin: false };
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('username', mockUser.username);
    localStorage.setItem('isAdmin', mockUser.isAdmin.toString());
    setIsAuthenticated(true);
    setUsername(mockUser.username);
    setIsAdmin(mockUser.isAdmin);
  };

  const register = async (email: string, password: string, username: string): Promise<void> => {
    // Mock registration
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('username', username);
    localStorage.setItem('isAdmin', 'false');
    setIsAuthenticated(true);
    setUsername(username);
    setIsAdmin(false);
  };

  const logout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('isAdmin');
    setIsAuthenticated(false);
    setUsername(null);
    setIsAdmin(false);
  };

  const value: AuthContextType = {
    isAuthenticated,
    username,
    isAdmin,
    login,
    loginWithProvider,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 